/**
 * Interventions — where the Brief bound, settled by the planner.
 *
 * Chris, 2026-09-04: "I'd rather this list of fixes came up at the end of the
 * modelling … there needs to be a mitigation offered — so the user can click
 * and fix, then re-build."
 *
 * Chris, 2026-10-28: no longer a pop-up (it blocked building) nor a button on
 * the Recommendation tab (that tab is gone). It is an "Interventions"
 * dropdown on the stage bar after Results, shown only while a built month is
 * outside the Brief, and opened only when the planner clicks it. The planner
 * picks a choice for each point in the one panel, then presses "Continue with
 * modelling": the chosen Brief changes are applied together and the months
 * they settle rebuild.
 *
 * Every month is BUILT and valued regardless — this is not "unfinished work",
 * it is the list of points where a rule of the Brief bound and what the
 * planner can change. Each choice is a computed Brief change (seasonBuild.ts::
 * colourIssues). Computed figures only (law 8).
 */
import { useEffect, useRef, useState } from 'react'
import type { MonthFixChange, MonthIssue } from './seasonBuild'

export interface ReviewMonth {
  monthKey: string
  monthLabel: string
  issues: MonthIssue[]
}

const KIND_LABEL: Record<MonthIssue['kind'], string> = {
  home: 'Getting everyone home',
  fleet: 'The fleet',
  paths: 'Network Rail paths',
  stock: 'Stock balance',
  crossing: 'Single-line crossings',
  loco: 'Locomotives',
}
/** What to do where no Brief change settles a point, in plain English. */
const NO_FIX: Record<MonthIssue['kind'], string> = {
  home: 'No Scope change settles this — a working must come out of this month\'s timetable.',
  fleet: 'No Scope change settles this — a working must come out of this month\'s timetable.',
  paths: 'No Scope change settles this — paths are physics; a working must come out of this month\'s timetable.',
  stock: 'No Scope change settles this — a working must come out of this month\'s timetable.',
  crossing: 'No Scope change settles this — a working must be retimed or come out of this month\'s timetable.',
  loco: 'No Scope change settles this — every engine starts and ends its day at Grosmont or Pickering. The timetable needs a working to bring this engine there or take it home: add, retime or withdraw a working on the Modelling stage, then rebuild.',
}

/**
 * Several chosen changes as ONE Brief change: the higher allowance and fleet
 * caps win, and a diesel for empty-stock moves is on if any choice turns it on.
 */
export function mergeFixChanges(changes: MonthFixChange[]): MonthFixChange {
  const out: MonthFixChange = {}
  const hi = (a: number | undefined, b: number | undefined) => (a == null ? b : b == null ? a : Math.max(a, b))
  for (const c of changes) {
    out.maxUnreturned = hi(out.maxUnreturned, c.maxUnreturned)
    out.maxSteam = hi(out.maxSteam, c.maxSteam)
    out.maxDiesel = hi(out.maxDiesel, c.maxDiesel)
    out.maxEcsDiesel = hi(out.maxEcsDiesel, c.maxEcsDiesel)
    if (c.ecsDiesel != null) out.ecsDiesel = (out.ecsDiesel ?? false) || c.ecsDiesel
  }
  for (const k of Object.keys(out) as (keyof MonthFixChange)[]) if (out[k] == null) delete out[k]
  return out
}

export interface InterventionsMenuProps {
  months: ReviewMonth[]
  busy: boolean
  /** Apply the merged Brief change and rebuild the months it settles. */
  onContinue: (change: MonthFixChange, monthKeys: string[]) => void
}

export function InterventionsMenu({ months, busy, onContinue }: InterventionsMenuProps) {
  const [open, setOpen] = useState(false)
  /** `${monthKey}|${issueIndex}` → the chosen fix's index. */
  const [picks, setPicks] = useState<Record<string, number>>({})
  const wrap = useRef<HTMLDivElement>(null)
  const total = months.reduce((t, m) => t + m.issues.length, 0)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    const onDown = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown) }
  }, [open])
  // A rebuild changes the list — start the next round with nothing chosen.
  // Keyed on the content: the orchestrator hands a fresh array every render.
  const sig = months.map((m) => `${m.monthKey}:${m.issues.map((i) => i.text).join('|')}`).join('/')
  useEffect(() => { setPicks({}) }, [sig])

  if (total === 0) return null

  const chosen = months.flatMap((m) => m.issues.flatMap((iss, i) => {
    const f = picks[`${m.monthKey}|${i}`]
    return f != null && iss.fixes[f] ? [{ monthKey: m.monthKey, change: iss.fixes[f].change }] : []
  }))
  const go = () => {
    if (chosen.length === 0) return
    onContinue(mergeFixChanges(chosen.map((c) => c.change)), [...new Set(chosen.map((c) => c.monthKey))])
    setOpen(false)
  }

  return (
    <div className="sh-interv" ref={wrap}>
      <button type="button" className={`sh-interv-btn${open ? ' sh-interv-btn--open' : ''}`} aria-expanded={open} aria-haspopup="dialog"
        onClick={() => setOpen(!open)} title={`${total} point${total === 1 ? '' : 's'} where the Scope bound — click to settle them`}>
        ⚠ Interventions <span className="sh-interv-count">{total}</span> ▾
      </button>
      {open && (
        <div className="sh-interv-panel" role="dialog" aria-label="Interventions">
          <div className="sh-interv-head">These are issues which need user interventions</div>
          <p className="stu-note" style={{ margin: '0 0 8px' }}>
            Every month is built and valued. Each point below is where a rule of your Scope bound the design. Pick a choice for the
            points you want to settle, then <strong>Continue with modelling</strong> — the Scope changes and those months rebuild.
            Leave a point unpicked and its month keeps the design shown, labelled.
          </p>
          {months.map((m) => (
            <section key={m.monthKey} className="sh-interv-month">
              <div className="sh-interv-month-title">{m.monthLabel}</div>
              {m.issues.map((iss, i) => {
                const key = `${m.monthKey}|${i}`
                return (
                  <div key={i} className="sh-interv-issue">
                    <div><span className="sh-prov sh-prov--assumed">{KIND_LABEL[iss.kind]}</span> <span>{iss.text}</span></div>
                    {iss.fixes.length === 0
                      ? <div className="stu-note">{NO_FIX[iss.kind]}</div>
                      : (
                        <div className="sh-interv-choices">
                          {iss.fixes.map((f, j) => (
                            <label key={j} className={`sh-interv-choice${picks[key] === j ? ' is-on' : ''}`}>
                              <input type="radio" name={key} checked={picks[key] === j} disabled={busy}
                                onChange={() => setPicks((p) => ({ ...p, [key]: j }))} />
                              {f.label}
                            </label>
                          ))}
                          <label className={`sh-interv-choice${picks[key] == null ? ' is-on' : ''}`}>
                            <input type="radio" name={key} checked={picks[key] == null} disabled={busy}
                              onChange={() => setPicks((p) => { const n = { ...p }; delete n[key]; return n })} />
                            Leave as it is
                          </label>
                        </div>
                      )}
                  </div>
                )
              })}
            </section>
          ))}
          <div className="sh-interv-foot">
            <span className="stu-note">{chosen.length === 0 ? 'Nothing chosen yet.' : `${chosen.length} choice${chosen.length === 1 ? '' : 's'} — rebuilds ${[...new Set(chosen.map((c) => c.monthKey))].length} month${new Set(chosen.map((c) => c.monthKey)).size === 1 ? '' : 's'}.`}</span>
            <button type="button" className="sh-sbtn sh-sbtn--build" disabled={busy || chosen.length === 0} onClick={go}>
              {busy ? 'Rebuilding…' : 'Continue with modelling'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
